<!-- LogoSpinner.svelte -->
<script>
    // Import the logo image
    import logoImage from "../assets/images/logos/united-logo.png";
    import { onMount, onDestroy } from "svelte";

    // Optional props with defaults
    export let size = "150px"; // Size of the spinner
    export let speed = "2s"; // Speed of rotation
    export let alt = "Loading..."; // Alt text for accessibility
    export let thickness = "6px"; // Thickness of the circular track
    export let trackColor = "#f0f0f0"; // Color of the loading track
    export let progressColor = "#6A0032"; // Color of the loading indicator
    export let loadingDuration = 3000; // Duration in ms for the progress bar to complete (0-100%)

    // Progress state
    let progress = 0;
    let animationStartTime;
    let animationFrameId;

    // Circle dimensions
    const radius = 45;
    const circumference = 2 * Math.PI * radius;

    function updateProgress(timestamp) {
        if (!animationStartTime) animationStartTime = timestamp;

        const elapsedTime = timestamp - animationStartTime;

        // Calculate progress as a percentage of elapsed time
        if (elapsedTime < loadingDuration) {
            progress = (elapsedTime / loadingDuration) * 100;
            animationFrameId = requestAnimationFrame(updateProgress);
        } else {
            // Ensure we end exactly at 100%
            progress = 100;
        }
    }

    onMount(() => {
        // Use requestAnimationFrame for smoother animation
        animationFrameId = requestAnimationFrame(updateProgress);
    });

    onDestroy(() => {
        // Clean up animation frame when component is destroyed
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
    });
</script>

<div
    class="spinner-container"
    style="--size: {size}; --thickness: {thickness}; --track: {trackColor}; --progress: {progressColor}; --speed: {speed};"
>
    <!-- Progress circle -->
    <svg class="progress-circle" viewBox="0 0 100 100">
        <circle class="progress-track" cx="50" cy="50" r={radius} />
        <circle
            class="progress-indicator"
            cx="50"
            cy="50"
            r={radius}
            stroke-dasharray={circumference}
            stroke-dashoffset={circumference - (circumference * progress) / 100}
        />
    </svg>

    <div class="logo-container">
        <img src={logoImage} {alt} class="logo" />
    </div>
</div>

<style>
    .spinner-container {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: var(--size);
        height: var(--size);
        perspective: 1000px; /* For 3D effect */
    }

    .progress-circle {
        position: absolute;
        width: 100%;
        height: 100%;
        transform: rotate(-90deg); /* Start from the top */
    }

    .progress-track {
        fill: none;
        stroke: var(--track);
        stroke-width: var(--thickness);
    }

    .progress-indicator {
        fill: none;
        stroke: var(--progress);
        stroke-width: var(--thickness);
        transition: stroke-dashoffset 0.05s linear;
        transform-origin: center;
    }

    .logo-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: calc(100% - 2 * var(--thickness));
        height: calc(100% - 2 * var(--thickness));
        animation: spin-y var(--speed) linear infinite;
        transform-style: preserve-3d;
    }

    .logo {
        max-width: 80%;
        max-height: 80%;
    }

    @keyframes spin-y {
        0% {
            transform: rotateY(0deg);
        }
        100% {
            transform: rotateY(360deg);
        }
    }
</style>
